import { useCallback, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";

import LoadingButton from "@mui/lab/LoadingButton";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useTheme from "@mui/material/styles/useTheme";

import { merge } from "lodash";

import CheckboxFormField from "components/checkbox-form-field";
import MicrophoneFormField from "components/microphone-form-field";
import SliderFormField from "components/slider-form-field";
import SpeakerFormField from "components/speaker-form-field";
import { type VoiceUserSettingsValues } from "features/administration/users/components/user-tabs/voice-user-settings-tab/voice-user-settings-tab.component";
import {
  DEFAULT_VOICE_USER_VALUES,
  formatVoiceUserSettingsInput
} from "features/administration/users/components/user-tabs/voice-user-settings-tab/voice-user-settings.utils";
import useVoiceUserSettings from "hooks/use-voice-user-settings.hook";

import useSunoDialer from "../use-suno-dialer.hook";

export default function VoiceSettingsWidget() {
  const theme = useTheme();

  const { handleToggleShowSettings } = useSunoDialer();
  const { voiceSettings, handleChangeSettings } = useVoiceUserSettings();

  const defaultValues: VoiceUserSettingsValues = useMemo(() => {
    return merge({}, DEFAULT_VOICE_USER_VALUES, voiceSettings);
  }, [voiceSettings]);

  const formMethods = useForm<VoiceUserSettingsValues>({
    mode: "onBlur",
    defaultValues: defaultValues
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
    setValue,
    reset,
    watch
  } = formMethods;

  const [default_microphone, default_speaker] = watch([
    "default_microphone",
    "default_speaker"
  ]);

  useEffect(() => {
    reset(defaultValues);
  }, [reset, defaultValues]);

  const onSubmit = useCallback(
    (formData: VoiceUserSettingsValues) => {
      const update_data = {
        id: voiceSettings.id,
        ...formatVoiceUserSettingsInput(formData)
      };

      handleChangeSettings(update_data);
      handleToggleShowSettings();
    },
    [handleChangeSettings, handleToggleShowSettings, voiceSettings.id]
  );

  return (
    <div
      style={{
        position: "absolute",
        right: 30,
        bottom: 20,
        zIndex: theme.zIndex.snackbar + 1
      }}
    >
      <form id="administration-user-form" onSubmit={handleSubmit(onSubmit)}>
        <Box
          sx={{
            maxWidth: "410px",
            background: `hsla(272, 18%, 48%, 0.85)`,
            boxShadow: theme.shadows[3],
            borderRadius: 2
          }}
        >
          <Grid container gap={3} sx={{ pb: 2 }}>
            <Grid item xs={12} mt={2}>
              <Typography ml={2} fontSize="20px" fontWeight={700} color="white">
                Device Settings
              </Typography>
            </Grid>
            <Grid item xs={11}>
              <MicrophoneFormField
                sx={{ pl: 2 }}
                label="Default microphone"
                value={default_microphone}
                color="white"
                onChange={(value: string) => {
                  setValue("default_microphone", value);
                }}
              />
            </Grid>
            <Grid item xs={11}>
              <SpeakerFormField
                sx={{ pl: 2 }}
                label="Default speaker"
                value={default_speaker}
                color="white"
                onChange={(value: string) => {
                  setValue("default_speaker", value);
                }}
              />
            </Grid>

            <Grid item xs={10.7} sx={{ mt: -1 }}>
              <SliderFormField
                control={control}
                label="Volume"
                name="default_volume"
                color="white"
                sx={{ ml: 2, mt: 2 }}
              />
            </Grid>
            <Grid item xs={11}>
              <FormControlLabel
                label="Do not disturb"
                sx={{ color: "white" }}
                control={
                  <CheckboxFormField
                    name="do_not_disturb"
                    control={control}
                    testId="do_not_disturb"
                    sx={{
                      ml: 2,
                      borderColor: "white",
                      "& .MuiSvgIcon-root": {
                        color: "white !important"
                      },
                      "&.Mui-checked .MuiSvgIcon-root": {
                        color: "white"
                      },
                      "&:hover .MuiSvgIcon-root": {
                        color: "white"
                      }
                    }}
                  />
                }
              />
            </Grid>
            <Grid item xs={12} sx={{ px: 2 }}>
              <Stack
                direction="row"
                width="100%"
                justifyContent="space-between"
              >
                <Button
                  size="small"
                  sx={{
                    mt: 2,
                    width: "max-content",
                    color: "white",
                    border: "solid 1px white"
                  }}
                  onClick={handleToggleShowSettings}
                >
                  Cancel
                </Button>
                <LoadingButton
                  variant="contained"
                  disabled={!isValid}
                  loading={isSubmitting}
                  type="submit"
                  sx={{ mt: 2, width: "max-content" }}
                >
                  Save Changes
                </LoadingButton>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </form>
    </div>
  );
}
